import { extend, override } from 'flarum/common/extend';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostListItem from 'flarum/forum/components/PostListItem';
import PostListState from 'flarum/forum/states/PostListState';

// Hides the "technical" post that carries a player's card-play turn.
//
// When a player picks cards in the slider, addCardSlider.tsx submits a
// regular comment post containing [arena-card=N] tags. Formatter/Configure.php
// turns these into <span class="ArenaPlayedCard" data-card-id="N"> markup.
// The backend's ProcessCardPlay still needs this post to exist as a normal
// comment (it parses the content to apply the turn), so it stays a real post
// server-side — this module only hides it visually. The engine immediately
// creates a system post (arenaBattleWait/arenaBattleTurn) right after it,
// which is what actually shows the turn to the user.
//
// Detection used to rely on a regex over contentHtml(), which was unreliable.
// The backend now flags these posts directly via the isArenaCardPlay
// attribute (extend.php -> PostResource).
//
// Note: extend(PostListItem.prototype, 'view', ...) returning null from the
// callback does NOT suppress rendering — extend() always returns the
// original method's result regardless of what the callback returns. extend()
// only works for mutating an object in place (like attrs.className below).
// override() is required to actually replace the return value.

// isArenaCardPlay is the reliable backend flag; the contentHtml() check is a
// fallback in case of a stale API response — both are OR'd together.
function isCardPlayPost(post) {
  var _post$attribute;
  if (!post) return false;
  if ((_post$attribute = post.attribute) != null && _post$attribute.call(post, 'isArenaCardPlay')) return true;
  const html = typeof post.contentHtml === 'function' ? post.contentHtml() : '';
  return typeof html === 'string' && html.includes('ArenaPlayedCard');
}
export default function hideCardPlayPosts() {
  // Inside the battle discussion: visually hide the post (see forum.less
  // .ArenaCardPlayPost--hidden) via a CSS class.
  extend(CommentPost.prototype, 'elementAttrs', function (attrs) {
    var _this$attrs;
    const post = (_this$attrs = this.attrs) == null ? void 0 : _this$attrs.post;
    if (isCardPlayPost(post)) {
      attrs.className = "".concat(attrs.className || '', " ArenaCardPlayPost--hidden").trim();
    }
    return attrs;
  });

  // On the user's profile "Posts" tab (and anywhere else backed by
  // PostListState, e.g. sitewide post search): drop card-play posts from
  // the page's item list before PostList ever builds a row for them.
  //
  // Returning null from PostListItem's view (below) only blanks out the
  // *contents* of a row — PostList.tsx wraps every item in its own <li>
  // *before* rendering PostListItem, so a null-content item still leaves an
  // empty <li> in the feed, i.e. a visible gap. Filtering here removes the
  // post from the list itself, so no <li> is created for it at all and the
  // feed just shows a continuous run of real posts.
  //
  // extend() can't change getPages()'s return value directly (see note
  // above), but the returned Page objects are mutated in place: reassigning
  // pg.items is enough since PostList reads that same object afterwards.
  extend(PostListState.prototype, 'getPages', function (pages) {
    for (const page of pages) {
      page.items = page.items.filter(post => !isCardPlayPost(post));
    }
  });

  // Belt-and-suspenders for any other place PostListItem is used directly
  // with a list that ISN'T backed by PostListState (so the filter above
  // wouldn't apply): still blank the row out via override(), same as before.
  override(PostListItem.prototype, 'view', function (original) {
    var _this$attrs2;
    const post = (_this$attrs2 = this.attrs) == null ? void 0 : _this$attrs2.post;
    if (isCardPlayPost(post)) {
      return null;
    }
    return original();
  });
}